import { ICE_SIZE, type Level, type RunState } from '@xox/shared'
import { memo, type ReactNode, useId } from 'react'
import { spriteFor, type TileLook, tileCtx } from './tiles'

/** Cell size in the layer's own coordinate space (viewBox is 0..100). */
const CELL = 100 / ICE_SIZE
/** Symbols are drawn in a 40×40 box and scaled down onto each cell. */
const S = 40

type Symbol =
  | 'ice'
  | 'sheen'
  | 'rock'
  | 'rock-rim'
  | 'rock-ghost'
  | 'hole'
  | 'crack'
  | 'arrow'
  | 'key'
  | 'door'
  | 'door-light'
  | 'halo'
  | 'padlock'

/** One drawing instruction for a cell. */
interface Layer {
  sym: Symbol
  rotate?: number
  opacity?: number
  /** Inherited by symbols that use currentColor (the door halo). */
  color?: string
  filter?: string
}

const AMBER = 'var(--ice-brass)'
const GREEN = 'var(--ice-glow)'

/** Expands the pure `TileLook` into concrete symbol stacks. */
function layersFor(look: TileLook): Layer[] {
  if (look.base === 'rock') return [{ sym: 'rock' }]
  // Flicker rocks keep both states in the DOM so the phase change cross-fades.
  if (look.base === 'flicker-solid') {
    return [
      { sym: 'ice' },
      { sym: 'rock', opacity: 1 },
      { sym: 'rock-rim' },
      { sym: 'rock-ghost', opacity: 0 },
    ]
  }
  const out: Layer[] = [{ sym: 'ice' }]
  switch (look.overlay) {
    case undefined:
      break
    case 'flicker-ghost':
      out.push({ sym: 'rock', opacity: 0 }, { sym: 'rock-ghost', opacity: 1 })
      break
    case 'arrow':
      out.push({ sym: 'arrow', rotate: look.rotate ?? 0 })
      break
    case 'door':
      out.push({ sym: 'halo', color: AMBER, opacity: 0.35 }, { sym: 'door' })
      break
    case 'door-locked':
      out.push({ sym: 'door', filter: 'saturate(.3)' }, { sym: 'padlock' })
      break
    case 'door-open':
      out.push({ sym: 'halo', color: GREEN, opacity: 0.55 }, { sym: 'door' }, { sym: 'door-light' })
      break
    default:
      out.push({ sym: look.overlay })
  }
  return out
}

/** Every symbol body, keyed so TypeScript insists each one exists. Drawn in a 0..40 box. */
const ART: Record<Symbol, ReactNode> = {
  ice: (
    <>
      <rect width={S} height={S} fill="var(--ice-surface)" />
      <rect
        x=".5"
        y=".5"
        width={S - 1}
        height={S - 1}
        fill="none"
        stroke="var(--ice-surface-deep)"
      />
      <path
        d="M2.5 37 V2.5 H37"
        fill="none"
        stroke="var(--ice-sheen)"
        strokeWidth="1"
        opacity=".45"
      />
    </>
  ),
  sheen: (
    <>
      <path d="M9 15 L20 8" stroke="var(--ice-sheen)" strokeWidth="2" strokeLinecap="round" />
      <circle cx="24" cy="7" r="1.3" fill="var(--ice-sheen)" />
    </>
  ),
  rock: (
    <>
      <rect width={S} height={S} fill="var(--ice-rock)" />
      <rect width={S} height="5" fill="var(--ice-rock-light)" />
      <rect y="34" width={S} height="6" fill="var(--ice-rock-shade)" />
      <path
        d="M0 20 H40 M14 5 V20 M28 20 V34"
        stroke="var(--ice-rock-shade)"
        strokeWidth="1.5"
        opacity=".7"
      />
      <path
        d="M0 5.75 H40 M0 20.75 H40"
        stroke="var(--ice-rock-light)"
        strokeWidth=".8"
        opacity=".35"
      />
    </>
  ),
  'rock-rim': (
    <rect
      x="4"
      y="4"
      width="32"
      height="32"
      rx="3"
      fill="none"
      stroke="var(--ice-surface)"
      strokeWidth="1.5"
      strokeDasharray="4 3"
      opacity=".7"
    />
  ),
  'rock-ghost': (
    <>
      <rect x="5" y="5" width="30" height="30" rx="4" fill="var(--ice-rock)" opacity=".18" />
      <rect
        x="5"
        y="5"
        width="30"
        height="30"
        rx="4"
        fill="none"
        stroke="var(--ice-rock)"
        strokeWidth="2"
        strokeDasharray="4 3"
        opacity=".7"
      />
    </>
  ),
  hole: (
    <>
      <ellipse cx="20" cy="21" rx="15" ry="13" fill="var(--ice-hole-rim)" />
      <ellipse cx="20" cy="22" rx="12.5" ry="10.5" fill="var(--ice-hole)" />
      <path
        d="M10 25 Q20 34 30 25"
        fill="none"
        stroke="var(--ice-hole-rim)"
        strokeWidth="1.5"
        opacity=".6"
      />
      <path
        d="M7 16 Q14 11 22 13"
        fill="none"
        stroke="var(--ice-sheen)"
        strokeWidth="1"
        opacity=".4"
      />
    </>
  ),
  crack: (
    <>
      <path
        d="M5 24 L12 19 L17 25 L23 15 L28 22 L35 16"
        fill="none"
        stroke="var(--ice-crack)"
        strokeWidth="1.8"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <path
        d="M17 25 L15 32 M23 15 L26 8"
        fill="none"
        stroke="var(--ice-crack)"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </>
  ),
  arrow: (
    <>
      <path d="M20 7 L32 21 H25 V33 H15 V21 H8 Z" fill="var(--ice-arrow)" opacity=".9" />
      <path
        d="M20 9.5 L30 21 H23.5"
        fill="none"
        stroke="var(--ice-sheen)"
        strokeWidth="1.2"
        opacity=".7"
      />
    </>
  ),
  key: (
    <g transform="rotate(-35 20 20)">
      <circle cx="13" cy="20" r="5.5" fill="none" stroke="var(--ice-brass)" strokeWidth="3.5" />
      <rect x="17" y="18.5" width="16" height="3" rx="1" fill="var(--ice-brass)" />
      <rect x="27" y="21" width="2.5" height="4" fill="var(--ice-brass)" />
      <rect x="31" y="21" width="2.5" height="3" fill="var(--ice-brass)" />
    </g>
  ),
  door: (
    <>
      <rect x="9" y="5" width="22" height="31" rx="3" fill="var(--ice-wood-dark)" />
      <rect x="11.5" y="7.5" width="17" height="28.5" rx="2" fill="var(--ice-wood)" />
      <path
        d="M16 10 V33 M20 10 V33 M24 10 V33"
        stroke="var(--ice-wood-dark)"
        strokeWidth="1"
        opacity=".5"
      />
      <circle cx="25" cy="22" r="1.8" fill="var(--ice-brass)" />
    </>
  ),
  'door-light': (
    <rect x="13.5" y="9.5" width="13" height="24" rx="1.5" fill="var(--ice-glow)" opacity=".6" />
  ),
  halo: <rect x="4" y="1" width="32" height="38" rx="7" fill="currentColor" />,
  padlock: (
    <>
      <path
        d="M16.5 20 V16.5 a3.5 3.5 0 0 1 7 0 V20"
        fill="none"
        stroke="var(--ice-rock-shade)"
        strokeWidth="2"
      />
      <rect x="14" y="19.5" width="12" height="9.5" rx="2" fill="var(--ice-brass)" />
      <circle cx="20" cy="24" r="1.3" fill="var(--ice-rock-shade)" />
    </>
  ),
}

interface Props {
  level: Level
  run?: RunState | null
  doorReached: boolean
}

/** All 100 tiles in one SVG: symbols defined once, one `<use>` per layer per cell. */
export const TileLayer = memo(function TileLayer({ level, run, doorReached }: Props) {
  const prefix = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const ctx = tileCtx(level, run, doorReached)
  return (
    <svg viewBox="0 0 100 100" className="absolute inset-0 size-full" aria-hidden>
      <defs>
        {(Object.keys(ART) as Symbol[]).map((sym) => (
          <symbol key={sym} id={`${prefix}-${sym}`} viewBox={`0 0 ${S} ${S}`}>
            {ART[sym]}
          </symbol>
        ))}
      </defs>
      {level.rows.flatMap((row, r) =>
        [...row].map((tile, c) => {
          const x = c * CELL
          const y = r * CELL
          return layersFor(spriteFor(tile, r, c, ctx)).map((layer, i) => (
            <use
              // biome-ignore lint/suspicious/noArrayIndexKey: layer order is the identity
              key={`${r}-${c}-${i}`}
              href={`#${prefix}-${layer.sym}`}
              x={x}
              y={y}
              width={CELL}
              height={CELL}
              transform={
                layer.rotate ? `rotate(${layer.rotate} ${x + CELL / 2} ${y + CELL / 2})` : undefined
              }
              opacity={layer.opacity}
              className={
                layer.opacity !== undefined ? 'transition-opacity duration-200' : undefined
              }
              style={{ color: layer.color, filter: layer.filter }}
            />
          ))
        }),
      )}
    </svg>
  )
})
